<script setup>
import { computed } from 'vue'

const props = defineProps({
  value: {
    type: Number,
    default: 0,
  },
  limit: {
    type: Number,
    default: 0,
  },
  descriptionLabel: {
    type: String,
    default: 'messages',
  },
  compactDescriptionLabel: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'violet',
    validator: (value) => ['gray', 'violet'].includes(value),
  },
})

const toNonNegativeNumber = (value) => (Number.isFinite(value) ? Math.max(0, value) : 0)

const normalizedValue = computed(() => toNonNegativeNumber(props.value))
const normalizedLimit = computed(() => toNonNegativeNumber(props.limit))
const itemsLeft = computed(() => Math.max(0, normalizedLimit.value - normalizedValue.value))
const fillPercent = computed(() => {
  if (normalizedLimit.value <= 0) return 0

  return Math.trunc((itemsLeft.value / normalizedLimit.value) * 100)
})

const buildUsageLabel = (descriptionLabel) => {
  if (normalizedLimit.value <= 0) {
    return `no ${descriptionLabel} available this week`
  }

  return `${itemsLeft.value}/${normalizedLimit.value} ${descriptionLabel} left this week`
}
const usageLabel = computed(() => buildUsageLabel(props.descriptionLabel))
const compactUsageLabel = computed(() =>
  buildUsageLabel(props.compactDescriptionLabel || props.descriptionLabel),
)
</script>

<template>
  <div :class="['message-usage-bar', `message-usage-bar-${color}`]" :aria-label="usageLabel">
    <div class="message-usage-bar-fill" :style="{ width: `${fillPercent}%` }"></div>
    <span class="message-usage-label-full">{{ usageLabel }}</span>
    <span class="message-usage-label-compact">{{ compactUsageLabel }}</span>
  </div>
</template>

<style scoped>
.message-usage-bar {
  position: relative;
  min-width: 11rem;
  overflow: hidden;
  border: 1px solid var(--message-usage-border);
  background: var(--message-usage-background);
  border-radius: 999px;
  color: var(--message-usage-text);
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.42rem 0.75rem;
  text-align: center;
}

.message-usage-bar-fill {
  position: absolute;
  inset: 0;
  width: 0;
  background: var(--message-usage-fill);
}

.message-usage-label-full,
.message-usage-label-compact {
  position: relative;
  z-index: 1;
}

.message-usage-label-compact {
  display: none;
}

.message-usage-bar-gray {
  --message-usage-border: rgba(209, 213, 219, 0.26);
  --message-usage-background: rgba(75, 85, 99, 0.34);
  --message-usage-fill: linear-gradient(90deg, #6b7280, #9ca3af);
  --message-usage-text: #f3f4f6;
}

.message-usage-bar-violet {
  --message-usage-border: rgba(196, 181, 253, 0.28);
  --message-usage-background: rgba(148, 163, 184, 0.24);
  --message-usage-fill: linear-gradient(90deg, #8b5cf6, #a78bfa);
  --message-usage-text: #f5f3ff;
}

@media (max-width: 860px) {
  .message-usage-bar {
    min-width: 0;
    padding-inline: 0.45rem;
    font-size: 0.72rem;
  }

  .message-usage-label-full {
    display: none;
  }

  .message-usage-label-compact {
    display: inline;
  }
}
</style>
